// ============================================
// Debug Page - API Testing (Owner Only)
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bug, 
  User, 
  BarChart3, 
  UserPlus, 
  CheckCircle, 
  XCircle, 
  Loader2,
  ArrowLeft,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import { useAuthStore } from '../stores';
import { cn } from '../lib/utils';

interface TestResult {
  status: 'idle' | 'loading' | 'success' | 'error';
  data?: unknown;
  error?: string;
  duration?: number;
}

// Use Worker directly for API testing
const API_BASE = 'https://crm-sales-os-api.bawaya-2019.workers.dev';

export function Debug() {
  const navigate = useNavigate();
  const { user, isAuthenticated, sessionToken } = useAuthStore();
  
  const [sessionCheck, setSessionCheck] = useState<TestResult>({ status: 'idle' });
  const [statsCheck, setStatsCheck] = useState<TestResult>({ status: 'idle' });
  const [leadsCheck, setLeadsCheck] = useState<TestResult>({ status: 'idle' });
  const [createLeadCheck, setCreateLeadCheck] = useState<TestResult>({ status: 'idle' });
  const [conversationsCheck, setConversationsCheck] = useState<TestResult>({ status: 'idle' });

  // Redirect non-owners
  if (!isAuthenticated || !user || !['owner', 'OWNER'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-4">Owner access required</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const runTest = async (
    name: string,
    setResult: (r: TestResult) => void,
    testFn: () => Promise<Response>
  ) => {
    setResult({ status: 'loading' });
    const start = Date.now();
    
    try {
      const response = await testFn();
      const duration = Date.now() - start;
      let data: unknown;
      try {
        data = await response.json();
      } catch {
        data = { raw: 'Could not parse response' };
      }
      
      if (response.ok) {
        setResult({ status: 'success', data, duration });
      } else {
        setResult({ status: 'error', error: `HTTP ${response.status}`, data, duration });
      }
    } catch (err) {
      const duration = Date.now() - start;
      setResult({ 
        status: 'error', 
        error: err instanceof Error ? err.message : 'Unknown error',
        duration 
      });
    }
  };

  const testSession = () => runTest('session', setSessionCheck, () => 
    fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' })
  );

  const testStats = () => runTest('stats', setStatsCheck, () => 
    fetch(`${API_BASE}/api/dashboard/stats`, { credentials: 'include' })
  );

  const testLeads = () => runTest('leads', setLeadsCheck, () => 
    fetch(`${API_BASE}/api/leads?limit=5`, { credentials: 'include' })
  );

  const testCreateLead = () => runTest('createLead', setCreateLeadCheck, () => 
    fetch(`${API_BASE}/api/leads`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: `+97250000${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        name: `Debug Test ${new Date().toISOString()}`,
        source: 'MANUAL',
        projectId: 'proj_hot_mobile'
      })
    })
  );

  const testConversations = () => runTest('conversations', setConversationsCheck, () => 
    fetch(`${API_BASE}/api/conversations?project_id=proj_hot_mobile&limit=5`, { credentials: 'include' })
  );

  const runAllTests = async () => {
    await testSession();
    await testStats();
    await testLeads();
    await testConversations();
  };

  const ResultCard = ({ 
    title, 
    icon, 
    result, 
    onRun 
  }: { 
    title: string; 
    icon: React.ReactNode; 
    result: TestResult; 
    onRun: () => void;
  }) => (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-medium text-white">{title}</h3>
        </div>
        <button 
          onClick={onRun}
          disabled={result.status === 'loading'}
          className="btn-secondary text-xs py-1 px-2"
        >
          {result.status === 'loading' ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            'Run'
          )}
        </button>
      </div>
      
      <div className={cn(
        "rounded-lg p-3 text-sm font-mono",
        result.status === 'idle' && "bg-slate-700/50 text-slate-400",
        result.status === 'loading' && "bg-blue-900/30 text-blue-300",
        result.status === 'success' && "bg-green-900/30 text-green-300",
        result.status === 'error' && "bg-red-900/30 text-red-300"
      )}>
        {result.status === 'idle' && 'Not tested yet'}
        {result.status === 'loading' && 'Testing...'}
        {result.status === 'success' && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4" />
              <span>Success ({result.duration}ms)</span>
            </div>
            <pre className="text-xs overflow-auto max-h-40">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        )}
        {result.status === 'error' && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-4 h-4" />
              <span>{result.error} ({result.duration}ms)</span>
            </div>
            {result.data !== undefined && (
              <pre className="text-xs overflow-auto max-h-40">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="btn-secondary p-2">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Bug className="w-6 h-6 text-amber-500" />
                Debug Panel
              </h1>
              <p className="text-slate-400 text-sm">API & Auth Testing</p>
            </div>
          </div>
          <button onClick={runAllTests} className="btn-primary">
            <RefreshCw className="w-4 h-4" />
            Run All Tests
          </button>
        </div>

        {/* Session Info */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4 mb-6">
          <h3 className="font-medium text-white mb-3">Current Session</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400">Authenticated:</span>
              <span className={cn("ml-2", isAuthenticated ? "text-green-400" : "text-red-400")}>
                {isAuthenticated ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <span className="text-slate-400">User ID:</span>
              <span className="ml-2 text-white font-mono">{user?.id || 'N/A'}</span>
            </div>
            <div>
              <span className="text-slate-400">Role:</span>
              <span className="ml-2 text-white">{user?.role || 'N/A'}</span>
            </div>
            <div>
              <span className="text-slate-400">Session Token:</span>
              <span className="ml-2 text-white font-mono text-xs">
                {sessionToken ? `${sessionToken.substring(0, 20)}...` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Test Cards */}
        <div className="grid gap-4">
          <ResultCard 
            title="GET /api/auth/me" 
            icon={<User className="w-5 h-5 text-blue-400" />}
            result={sessionCheck}
            onRun={testSession}
          />
          
          <ResultCard 
            title="GET /api/dashboard/stats" 
            icon={<BarChart3 className="w-5 h-5 text-cyan-400" />}
            result={statsCheck}
            onRun={testStats}
          />
          
          <ResultCard 
            title="GET /api/leads" 
            icon={<User className="w-5 h-5 text-green-400" />}
            result={leadsCheck}
            onRun={testLeads}
          />
          
          <ResultCard 
            title="GET /api/conversations" 
            icon={<MessageSquare className="w-5 h-5 text-purple-400" />}
            result={conversationsCheck}
            onRun={testConversations}
          />
          
          <ResultCard 
            title="POST /api/leads (Create)" 
            icon={<UserPlus className="w-5 h-5 text-amber-400" />}
            result={createLeadCheck}
            onRun={testCreateLead}
          />
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-slate-500 text-sm">
          Debug page - Owner access only
        </div>
      </div>
    </div>
  );
}
